import React, { useState, useEffect } from 'react';
import { X, Eye, FileText, Package, TrendingUp } from 'lucide-react';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import InvoiceGenerator from './InvoiceGenerator';
import OrderSheetGenerator from './OrderSheetGenerator';

const CustomerDetails = ({ customer, onClose }) => {
  const [customerOrders, setCustomerOrders] = useState([]);
  const [customerSales, setCustomerSales] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);
  const [companyDetails, setCompanyDetails] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCustomerData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.id]);

  const loadCustomerData = async () => {
    try {
      const [ordersSnapshot, salesSnapshot, companySnapshot] = await Promise.all([
        getDocs(query(collection(db, 'orders'), where('customerId', '==', customer.id))),
        getDocs(query(collection(db, 'sales'), where('customerId', '==', customer.id))),
        getDocs(collection(db, 'company'))
      ]);
      
      const ordersData = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const salesData = salesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const companyData = companySnapshot.docs.length > 0 ? companySnapshot.docs[0].data() : {};
      
      setCustomerOrders(ordersData);
      setCustomerSales(salesData);
      setCompanyDetails(companyData);
    } catch (error) {
      console.error('Error loading customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#f39c12';
      case 'in-production': return '#3498db';
      case 'ready': return '#27ae60';
      case 'completed': return '#95a5a6';
      default: return '#666';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch(status) {
      case 'not_paid': return '#e74c3c';
      case 'half_paid': return '#f39c12';
      case 'full_paid': return '#27ae60';
      default: return '#666';
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h3>Loading customer details...</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ 
        maxWidth: '95vw', 
        width: '900px',
        maxHeight: '95vh',
        margin: '10px'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <h2 className="modal-title">{customer.shopName} - {customer.city}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={24} />
          </button>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr', 
          gap: '20px', 
          marginBottom: '20px' 
        }}>
          <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
            <Package size={24} color="#667eea" style={{ marginBottom: '8px' }} />
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333' }}>{customerOrders.length}</div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Total Orders</div>
          </div>
          <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
            <TrendingUp size={24} color="#27ae60" style={{ marginBottom: '8px' }} />
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333' }}>{customerSales.length}</div>
            <div style={{ color: '#666', fontSize: '0.9rem' }}>Total Sales</div>
          </div>
        </div>

        <div style={{ 
          display: 'flex', 
          flexDirection: window.innerWidth < 768 ? 'column' : 'row',
          gap: '20px', 
          height: window.innerWidth < 768 ? 'auto' : '500px' 
        }}>
          {/* Orders Section */}
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: window.innerWidth < 768 ? '300px' : 'auto'
          }}>
            <h3 style={{ marginBottom: '15px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Package size={20} /> Orders ({customerOrders.length})
            </h3>
            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: '8px', padding: '10px' }}>
              {customerOrders.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>No orders found</div>
              ) : (
                customerOrders.map(order => (
                  <div key={order.id} style={{
                    background: 'white',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '10px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div style={{ fontWeight: 'bold', color: '#333' }}>#{order.id.slice(-6)}</div>
                      <div style={{ 
                        padding: '4px 8px', 
                        borderRadius: '4px', 
                        fontSize: '0.8rem',
                        background: getStatusColor(order.status),
                        color: 'white'
                      }}>
                        {order.status.toUpperCase()}
                      </div>
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>
                      <div>Date: {new Date(order.createdAt).toLocaleDateString()}</div>
                      <div>Items: {order.items?.length} products</div>
                      <div>Total: LKR {order.total?.toLocaleString()}</div>
                      {order.items && (
                        <div style={{ marginTop: '5px' }}>
                          {order.items.map((item, idx) => (
                            <div key={idx} style={{ fontSize: '0.8rem', color: '#888', display: 'flex', justifyContent: 'space-between' }}>
                              <span>{item.articleNumber} - {item.colour}</span>
                              <span>
                                {item.status === 'delivered' ? '✓ Delivered' : 
                                 item.status === 'partial' ? `◐ ${item.deliveredQuantity || 0}/${item.quantity}` :
                                 item.productionStatus === 'completed' ? '✓ Ready' : '○ Pending'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => setSelectedOrder(order)}
                        style={{
                          background: '#667eea',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <FileText size={12} /> Order Sheet
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Sales Section */}
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: window.innerWidth < 768 ? '300px' : 'auto'
          }}>
            <h3 style={{ marginBottom: '15px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TrendingUp size={20} /> Sales ({customerSales.length})
            </h3>
            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: '8px', padding: '10px' }}>
              {customerSales.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>No sales found</div>
              ) : (
                customerSales.map(sale => (
                  <div key={sale.id} style={{
                    background: 'white',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '10px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div style={{ fontWeight: 'bold', color: '#333' }}>#{sale.id.slice(-6)}</div>
                      <div style={{ 
                        padding: '4px 8px', 
                        borderRadius: '4px', 
                        fontSize: '0.8rem',
                        background: getPaymentStatusColor(sale.paymentStatus),
                        color: 'white'
                      }}>
                        {(sale.paymentStatus || 'not_paid').replace('_', ' ').toUpperCase()}
                      </div>
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>
                      <div>Date: {new Date(sale.createdAt).toLocaleDateString()}</div>
                      <div>Items: {sale.items?.length} products</div>
                      <div>Amount: LKR {(sale.totalAmount || sale.total || 0).toLocaleString()}</div>
                      {sale.chequeNumber && (
                        <div style={{ fontSize: '0.8rem', color: '#888' }}>
                          Cheque: {sale.chequeNumber} {sale.chequeDate && `(${sale.chequeDate})`}
                        </div>
                      )}
                      {sale.isPartial && (
                        <div style={{ fontSize: '0.8rem', color: '#f39c12' }}>Partial Delivery</div>
                      )}
                      {sale.items && (
                        <div style={{ marginTop: '5px' }}>
                          {sale.items.map((item, idx) => (
                            <div key={idx} style={{ fontSize: '0.8rem', color: '#888', display: 'flex', justifyContent: 'space-between' }}>
                              <span>{item.articleNumber} - {item.colour}</span>
                              <span>{item.quantity} pairs collected</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => setSelectedSale(sale)}
                        style={{
                          background: '#27ae60',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Eye size={12} /> Invoice
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Summary */}
        <div style={{ 
          marginTop: '20px', 
          padding: '15px', 
          background: 'linear-gradient(45deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))', 
          borderRadius: '8px' 
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr 1fr', 
            gap: '15px', 
            textAlign: 'center' 
          }}>
            <div>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#667eea' }}>
                LKR {customerOrders.reduce((sum, order) => sum + (order.total || 0), 0).toLocaleString()}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666' }}>Total Orders Value</div>
            </div>
            <div>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#27ae60' }}>
                LKR {customerSales.reduce((sum, sale) => sum + (sale.totalAmount || sale.total || 0), 0).toLocaleString()}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666' }}>Total Sales Value</div>
            </div>
            <div>
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#e74c3c' }}>
                LKR {customerSales.filter(sale => sale.paymentStatus !== 'full_paid').reduce((sum, sale) => sum + (sale.totalAmount || sale.total || 0), 0).toLocaleString()}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666' }}>Pending Payments</div>
            </div>
          </div>
        </div>
      </div>

      {selectedOrder && (
        <OrderSheetGenerator 
          order={selectedOrder} 
          companyDetails={companyDetails} 
          onClose={() => setSelectedOrder(null)} 
        />
      )}

      {selectedSale && (
        <InvoiceGenerator 
          sale={selectedSale} 
          companyDetails={companyDetails} 
          onClose={() => setSelectedSale(null)} 
        />
      )}
    </div>
  );
};

export default CustomerDetails;